import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Alert, Button, Col, Drawer, Layout, Modal, Popconfirm, Row, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import { InvoiceFormBase, createBuyerInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import ResizableSplitPanel from '../common/ResizableSplitPanel';
import InvoiceUploadandParse, {
	InvoiceLoadingSkeletonAnimated,
} from '../invoice_uploader/InvoiceUploadandParse';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import {
	createInvoiceByInternalTech,
	fetchTaxLineItemsForBuyer,
	fetchTaxLineItemsForSupplier,
	getInvoiceNumber,
	getInvoiceNumberBuyer,
	invoicesRestCrudThunksForBuyer,
} from '../../thunks/invoices_thunks';
import {
	buildProjectInvoiceEntitySelects,
	projectInvoiceEntitySelectsDispatchToProps,
	projectInvoiceEntitySelectsStateToProps,
} from '../project_invoice_entity_selects/projectInvoiceEntitySelects';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import {
	applyParsedInvoiceToForm,
	buildInvoicePayloadFromFormValues,
	compareParsedWithFinal,
	getCurrencyMismatch,
} from '../../utils/InvoiceUtils';
import { getCurrency, getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import useMountEffect from '../../hooks/useMountEffect';

const { Content } = Layout;

/**
 * Creates an invoice against a project rather than a work order.
 *
 * The work-order flow (BuyerInvoiceAIForm) derives locationId / buyerFacilityId /
 * buyerCompanyId from the work order. A project (company-scoped, `Project.buyerCompanyId`)
 * has none of them, and the API requires all three on the payload, so:
 *   - Service Provider and Bill to location are fields on the invoice form itself, wired
 *     through the config's `entitySelects` so this page owns the thunks and the shared
 *     form stays role-agnostic.
 *   - buyerFacilityId is never asked for — it hangs off the chosen location.
 *   - buyerCompanyId comes off the project.
 */
const ProjectInvoiceAIForm: FC<any> = ({
	project,
	getProject,
	fetchLocations,
	fetchMultipleLocations,
	fetchSupplierFacilities,
	fetchMultipleSupplierFacilities,
	createInvoice,
	fetchTaxLineItems,
	checkInvoiceNumber,
	locations,
	supplierFacilities,
	currentUser,
	creating,
	updating,
	createErrors,
	updateErrors,
	userType,
}): React.ReactElement => {
	const [projectLoading, setProjectLoading] = useState(true);
	const [invoiceFormData, setInvoiceFormData] = useState<any>({});
	const [pdfUrl, setPdfUrl] = useState(null);
	const [formKey, setFormKey] = useState(0);
	// Bumped on user-initiated "start over" events (delete, reset, error, currency
	// mismatch). NOT bumped on parse-complete — remounting the uploader mid-S3 would
	// orphan the in-flight upload and we would lose the final invoicePDFLink.
	const [uploaderKey, setUploaderKey] = useState(0);
	// Only written to — applyParsedInvoiceToForm requires the setter.
	const [, setParsedDiscount] = useState(null);
	const [parsedFormDataSnapshot, setParsedFormDataSnapshot] = useState<any>(null);
	const [parsingLoading, setParsingLoading] = useState(false);
	const [showDrawer, setShowDrawer] = useState(false);
	const [disableCosts, setDisableCosts] = useState(false);

	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const projectId = nullSafeGet('params.id', match);

	useMountEffect(() => {
		getProject(projectId).finally(() => setProjectLoading(false));
		return undefined;
	});

	// Service Provider and Bill to location are fields on the invoice form itself, so
	// neither is known when the PDF is parsed. The currency the parsed totals are checked
	// against therefore comes from the buyer company rather than from a picked vendor.
	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);

	// Stands in for the work order in the parse adapter. There is no location yet, so the
	// dates printed on the PDF are read in the default timezone.
	const parseAnchor = useMemo(() => ({ location: undefined }), []);

	const handleS3UrlReady = useCallback((s3Url: string) => {
		setInvoiceFormData((prev) => ({ ...prev, invoicePDFLink: s3Url }));
		setPdfUrl(s3Url);
	}, []);

	const handleS3UploadError = useCallback(() => {
		message.error('Failed to upload invoice PDF. Please try uploading again.');
		setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
		setUploaderKey((prev) => prev + 1);
	}, []);

	const handleDeletePdf = useCallback(() => {
		if (parsedFormDataSnapshot) {
			posthog.capture('invoice_pdf_deleted_after_parsing', { user_type: userType });
		}
		setPdfUrl(null);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setDisableCosts(false);
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		setShowDrawer(false);
		setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
	}, [parsedFormDataSnapshot, userType]);

	const handleReset = useCallback(() => {
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setDisableCosts(false);
		// The uploaded PDF survives: "create from scratch" clears the parsed VALUES, it does
		// not un-upload the file, and the drawer stays open on top of the uploader. Dropping
		// the link here left a form that handleSubmit rejects on every attempt with "Invoice
		// PDF is still uploading", and closing the drawer to re-upload discards what was
		// just typed.
		setInvoiceFormData((prev) => ({ invoicePDFLink: nullSafeGet('invoicePDFLink', prev) }));
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		posthog.capture('invoice_form_reset', {
			user_type: userType,
			had_parsed_data: !!parsedFormDataSnapshot,
		});
	}, [userType, parsedFormDataSnapshot]);

	const handleFileSelected = useCallback((fileData) => {
		if (fileData.fileUrl) {
			setPdfUrl(fileData.fileUrl);
			setParsingLoading(true);
			setShowDrawer(true);
		}
	}, []);

	const handleUploadError = useCallback(
		(error: any = {}) => {
			message.error('Error parsing invoice. Please try again.');
			setParsingLoading(false);
			setShowDrawer(false);
			setFormKey((prev) => prev + 1);
			setUploaderKey((prev) => prev + 1);
			setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
			posthog.capture('invoice_pdf_parse_error', {
				user_type: userType,
				error: error.message,
			});
		},
		[userType]
	);

	const handleUploadSuccess = useCallback(
		(data) => {
			if (!data.parsedData) {
				return;
			}

			// Not checked here, deliberately: the comparison is against the VENDOR's currency
			// and the service provider is chosen after the upload, so the only currency
			// available at this point is the buyer company's — comparing against that would
			// reject (and delete) a valid invoice from a vendor who bills in another
			// currency. It is checked on submit instead, by which time a provider has been
			// picked and there is a real vendor currency to compare against.

			applyParsedInvoiceToForm({
				parsedData: data.parsedData,
				workOrder: undefined,
				anchor: parseAnchor,
				userType,
				setInvoiceFormData,
				setParsedFormDataSnapshot,
				setParsedDiscount,
				setDisableCosts,
			});
			setFormKey((prev) => prev + 1);
			setShowDrawer(true);
		},
		[parseAnchor, userType]
	);

	const onSuccess = useCallback(
		(record) => {
			history.replace(
				getLinkWIthBackLinkParams(
					{ ...location, pathname: `/${userType}/projects/detail/${projectId}/invoices` },
					'Back to Project',
					`/${userType}/invoices/detail/${nullSafeGet('id', record)}`
				)
			);
		},
		[history, location, userType, projectId]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			const currentInvoicePDFLink = nullSafeGet('invoicePDFLink', invoiceFormData);
			if (!currentInvoicePDFLink) {
				message.error('Invoice PDF is still uploading. Please wait a moment and try again.');
				return;
			}

			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			// The currency check that could not run at parse time. Now that a provider has
			// been chosen there is a real vendor currency to compare the parsed one against,
			// and an invoice parsed in one currency must not be filed against a vendor
			// billing in another — the amounts would simply be relabelled, not converted.
			const parsedData = nullSafeGet('AIData', invoiceFormData);
			const pickedSupplierFacility = getEntityById(
				formValues.supplierFacilityId,
				supplierFacilities
			);
			// Only when the parser actually read a currency. getCurrencyMismatch defaults a
			// missing one to USD, which would flag every non-USD provider as a mismatch on a
			// parse that never named a currency at all — and there would be no way through,
			// since the parsed data does not change as the user edits.
			const parsedCurrencyId = nullSafeGet('currency', parsedData);
			const currencyMismatch = parsedCurrencyId
				? getCurrencyMismatch(parsedData, getCurrency({ supplierFacility: pickedSupplierFacility }))
				: null;
			if (currencyMismatch) {
				// A warning, not a wall. The risk is filing parsed amounts under a currency they
				// were not written in — but a user who has re-keyed them in the provider's
				// currency has fixed exactly that, and the parsed metadata never changes to
				// reflect it, so blocking outright would trap them with no way out but
				// discarding their work.
				const proceedDespiteMismatch = await new Promise((resolve) => {
					Modal.confirm({
						title: 'Currency mismatch',
						content: `This invoice was parsed as ${currencyMismatch.parsedCurrency}, but the selected service provider bills in ${currencyMismatch.systemCurrency}. The amounts are not converted — continue only if you have re-entered them in ${currencyMismatch.systemCurrency}.`,
						okText: `Submit as ${currencyMismatch.systemCurrency}`,
						cancelText: 'Go back',
						width: '40vw',
						centered: true,
						onOk: () => resolve(true),
						onCancel: () => resolve(false),
					});
				});
				posthog.capture('project_invoice_currency_mismatch', {
					user_type: userType,
					parsed_currency: currencyMismatch.parsedCurrency,
					provider_currency: currencyMismatch.systemCurrency,
					submitted_anyway: proceedDespiteMismatch,
				});
				if (!proceedDespiteMismatch) {
					return;
				}
			}

			// Both come from the form. buyerFacilityId isn't asked for — it hangs off the
			// chosen location, so it's read back out of the slice the picker populated.
			const locationId = formValues.locationId;
			const supplierFacilityId = formValues.supplierFacilityId;
			const pickedLocation = getEntityById(locationId, locations);

			const invoice = buildInvoicePayloadFromFormValues(formValues, scope, {
				projectId,
				locationId,
				buyerCompanyId: nullSafeGet('buyerCompanyId', project),
				currentUser,
				role: userType === ROLE_TYPES.SUPPLIER ? 'supplier' : 'buyer',
				baseMeta: {
					invoicePDFLink: currentInvoicePDFLink,
					buyerFacilityId: nullSafeGet('buyerFacilityId', pickedLocation),
					supplierFacilityId,
				},
				status: formValues.status || 'pending',
			});

			if (formValues.signatureName) {
				invoice.aiParseMismatchSignedByEmail = currentUser.email;
			}

			if (parsedFormDataSnapshot) {
				const changeMetrics = compareParsedWithFinal(parsedFormDataSnapshot, invoice);
				const hadEdits =
					nullSafeGet('total_fields_changed', changeMetrics) > 0 ||
					nullSafeGet('total_line_items_changed', changeMetrics) > 0;
				posthog.capture('invoice_created_from_parsed_pdf', {
					user_type: userType,
					was_parsed: true,
					user_changed_fields: hadEdits,
					anchor: 'project',
					...changeMetrics,
				});
				invoice.isAiParsed = true;
				invoice.isAiParsedEdited = hadEdits;
			}

			const record = await createInvoice(invoice);
			onSuccess(record);
		},
		[
			invoiceFormData,
			projectId,
			project,
			locations,
			supplierFacilities,
			currentUser,
			userType,
			parsedFormDataSnapshot,
			createInvoice,
			onSuccess,
		]
	);

	const entitySelects = useMemo(
		() =>
			buildProjectInvoiceEntitySelects({
				locations,
				supplierFacilities,
				fetchLocations,
				fetchMultipleLocations,
				fetchSupplierFacilities,
				fetchMultipleSupplierFacilities,
			}),
		[
			locations,
			supplierFacilities,
			fetchLocations,
			fetchMultipleLocations,
			fetchSupplierFacilities,
			fetchMultipleSupplierFacilities,
		]
	);

	const formConfig = useMemo(() => {
		if (!currentUser || !currency) {
			return null;
		}
		return createBuyerInvoiceConfig(
			invoiceFormData,
			undefined,
			currentUser,
			history,
			handleSubmit,
			{
				creating,
				updating,
				createErrors,
				updateErrors,
				parsedDataSnapshot: parsedFormDataSnapshot,
				disableCosts,
				onReset: handleReset,
				fetchTaxLineItems,
				currency,
				checkInvoiceNumber,
				hideWorkOrderNumber: true,
				showTitle: true,
				entitySelects,
			}
		);
	}, [
		invoiceFormData,
		currentUser,
		history,
		handleSubmit,
		creating,
		updating,
		createErrors,
		updateErrors,
		parsedFormDataSnapshot,
		disableCosts,
		handleReset,
		fetchTaxLineItems,
		currency,
		checkInvoiceNumber,
		entitySelects,
	]);

	if (projectLoading) {
		return <PageLoadingPlaceholder />;
	}

	// buyerCompanyId is required on the payload and only the project has it, so there is
	// nothing to create against if the project didn't load.
	if (!nullSafeGet('id', project)) {
		return (
			<Content className="p-m">
				<Alert
					type="error"
					showIcon
					message="We couldn't load this project, so an invoice can't be created for it right now."
				/>
			</Content>
		);
	}

	return (
		<Content className="p-m">
			<InvoiceUploadandParse
				key={uploaderKey}
				handleUploadSuccess={handleUploadSuccess}
				onFileSelected={handleFileSelected}
				onParsingStart={() => setParsingLoading(true)}
				onParsingComplete={() => setParsingLoading(false)}
				onS3UrlReady={handleS3UrlReady}
				onS3UploadError={handleS3UploadError}
				uploaderType="dragger"
				onError={handleUploadError}
			/>

			<Drawer
				width="100vw"
				placement="right"
				visible={showDrawer}
				bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden' }}
				style={{ top: 0, height: '100vh' }}
				className="fullscreen-drawer"
				closeIcon={
					<Popconfirm
						title="Are you sure you want to go back? Any unsaved changes will be lost."
						onConfirm={handleDeletePdf}
						okText="Yes"
						cancelText="No"
					>
						<Button type="ghost" icon={<ArrowLeftOutlined />}>
							Back
						</Button>
					</Popconfirm>
				}
				title={<div className="h3medium">Create Invoice</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={pdfUrl}
										fileName="Invoice.pdf"
										showDelete={true}
										onDelete={handleDeletePdf}
									/>
								</div>
							}
							rightPanel={
								<div>
									{parsingLoading || !formConfig ? (
										<InvoiceLoadingSkeletonAnimated />
									) : (
										<InvoiceFormBase
											key={formKey}
											config={formConfig}
											handleCancel={handleDeletePdf}
										/>
									)}
								</div>
							}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	project: getEntityById(nullSafeGet('match.params.id', ownProps), state.projects),
	...projectInvoiceEntitySelectsStateToProps(state),
	creating: state.invoices.creating,
	updating: state.invoices.updating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const isSupplier = ownProps.userType === ROLE_TYPES.SUPPLIER;
	return {
		getProject: (id) =>
			dispatch(
				isSupplier
					? projectsRestCrudThunksForSupplier.readOne(id)
					: projectsRestCrudThunksForBuyer.readOne(id)
			),
		...projectInvoiceEntitySelectsDispatchToProps(dispatch, isSupplier),
		// Internal techs post through the by_internal_tech route, exactly as the work-order
		// AI form does for the supplier app. Both land on the same create validation.
		createInvoice: (invoice) =>
			dispatch(
				isSupplier
					? createInvoiceByInternalTech(invoice)
					: invoicesRestCrudThunksForBuyer.create(invoice)
			),
		fetchTaxLineItems: (currencyId: string) =>
			dispatch(
				isSupplier
					? fetchTaxLineItemsForSupplier(currencyId)
					: fetchTaxLineItemsForBuyer(currencyId)
			),
		checkInvoiceNumber: (invoice, supplierFacilityId) =>
			dispatch(
				isSupplier
					? getInvoiceNumber(invoice, supplierFacilityId)
					: getInvoiceNumberBuyer(invoice, supplierFacilityId)
			),
	};
};

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(ProjectInvoiceAIForm))
);
